import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useAuditLogs } from '@/hooks/useTeamManagement';
import { useTeamMembers } from '@/hooks/useTeamManagement';
import { ScrollText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatLocalizedDate } from '@/lib/localizedDate';

const ACTION_COLORS: Record<string, string> = {
  'user.invited': 'bg-info/10 text-info border-info/20',
  'user.role_changed': 'bg-warning/10 text-warning border-warning/20',
  'user.updated': 'bg-accent/10 text-accent border-accent/20',
  'user.deactivated': 'bg-destructive/10 text-destructive border-destructive/20',
  'user.reactivated': 'bg-success/10 text-success border-success/20',
  'user.permissions_overridden': 'bg-accent/10 text-accent border-accent/20',
};

export function AuditLogView() {
  const { t, i18n } = useTranslation();
  const { data: logs, isLoading } = useAuditLogs();
  const { data: members } = useTeamMembers();

  const getUserName = (userId: string | null) => {
    if (!userId) return t('hire.team.audit.system');
    const m = members?.find(m => m.user_id === userId);
    return m?.full_name || userId.slice(0, 8);
  };

  if (isLoading) {
    return <Card className="p-6 rounded-xl"><Skeleton className="h-64 w-full rounded-xl" /></Card>;
  }

  return (
    <Card className="rounded-xl shadow-card border-border">
      <div className="flex items-center gap-2 p-5 border-b border-border">
        <ScrollText className="w-5 h-5 text-accent" />
        <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.team.audit.title')}</h2>
        <span className="text-xs text-muted-foreground ms-auto">{t('hire.team.audit.lastEvents')}</span>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('hire.team.audit.columns.time')}</TableHead>
              <TableHead>{t('hire.team.audit.columns.actor')}</TableHead>
              <TableHead>{t('hire.team.audit.columns.action')}</TableHead>
              <TableHead>{t('hire.team.audit.columns.target')}</TableHead>
              <TableHead className="hidden md:table-cell">{t('hire.team.audit.columns.details')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!logs?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                  {t('hire.team.audit.empty')}
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap">
                    <span className="text-xs text-muted-foreground">
                      {formatLocalizedDate(log.created_at, 'MMM d, HH:mm', i18n.language)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getUserName(log.actor_user_id)}</span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs ${ACTION_COLORS[log.action] || 'bg-muted text-muted-foreground'}`}
                    >
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {log.target_type ? `${log.target_type}` : '—'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-xs text-muted-foreground font-mono max-w-xs truncate block">
                      {log.details && Object.keys(log.details).length > 0
                        ? JSON.stringify(log.details)
                        : '—'
                      }
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
